import { type CSSProperties } from "react";
import { Link, useNavigate } from "react-router";
import { useEntityTypes, useWorkspaceInfo } from "../../../lib/hooks/use-kuvio";
import { useRowDetail } from "../../hooks/use-row-detail";
import { displayFieldFor } from "../../../lib/query-keys";
import type { EntityRecord, EntityTypeDefinition } from "../../../lib/types";

/**
 * Sprint 12b — orientation chrome for the full-page row view. Renders
 * `[← Back] · Tables · {TableName} · {RowDisplayName}` above the detail content.
 * Each ancestor segment links out; Back uses browser history, falling back to
 * this row's table view when the page was opened from a shared/direct URL (no
 * in-app history to pop).
 *
 * Takes just slug + id and resolves names from already-cached queries
 * (`list_entity_types` for the type + table location, `get_row_detail` for the
 * row's display name), so integrating it is a two-line change in the full page.
 */
interface RowDetailBreadcrumbProps {
  entityTypeSlug: string;
  entityId: string;
}

export function RowDetailBreadcrumb({ entityTypeSlug, entityId }: RowDetailBreadcrumbProps) {
  const navigate = useNavigate();
  const entityTypes = useEntityTypes();
  const workspace = useWorkspaceInfo();
  const detail = useRowDetail(entityTypeSlug, entityId);

  const def = (entityTypes.data ?? []).find((t) => t.slug === entityTypeSlug);
  const rootNodeId = workspace.data?.root_node_id as string | undefined;

  const typeName = def?.name ?? entityTypeSlug;
  const tableNodeId = def?._table_node_id;
  const ownerAgentId = def?.owner_agent_id ?? rootNodeId;
  const tableViewUrl =
    tableNodeId && ownerAgentId ? `/agents/${ownerAgentId}/tables/${tableNodeId}` : undefined;

  const entity = detail.data?.entity as EntityRecord | undefined;
  const rowDisplayName = resolveDisplayName(entity, def, entityId);

  function handleBack() {
    // Opened directly (fresh tab / shared URL) → no in-app history to pop.
    if (window.history.length <= 1) {
      navigate(tableViewUrl ?? "/tables");
    } else {
      navigate(-1);
    }
  }

  return (
    <nav style={navStyle} aria-label="Breadcrumb">
      <button type="button" onClick={handleBack} style={backStyle} title="Back to the previous view">
        <i className="ti ti-arrow-left" style={{ fontSize: 13 }} aria-hidden="true" />
        Back
      </button>
      <Sep />
      <Link to="/tables" className="kuvio-crumb-link" style={segStyle}>
        Tables
      </Link>
      <Sep />
      {tableViewUrl ? (
        <Link to={tableViewUrl} className="kuvio-crumb-link" style={segStyle}>
          {typeName}
        </Link>
      ) : (
        <span style={segStyle}>{typeName}</span>
      )}
      <Sep />
      <span style={currentStyle} title={rowDisplayName}>
        {rowDisplayName}
      </span>
    </nav>
  );
}

function resolveDisplayName(
  entity: EntityRecord | undefined,
  def: EntityTypeDefinition | undefined,
  fallbackId: string,
): string {
  if (!entity) return fallbackId;
  const titleField = def?.fields.find((f) => f.display_role === "title");
  if (titleField && entity[titleField.name] != null && entity[titleField.name] !== "") {
    return String(entity[titleField.name]);
  }
  const df = def ? displayFieldFor(def) : undefined;
  if (df && entity[df] != null && entity[df] !== "") return String(entity[df]);
  return fallbackId;
}

function Sep() {
  return (
    <span style={{ color: "var(--color-text-tertiary)", opacity: 0.6 }} aria-hidden="true">
      ·
    </span>
  );
}

const navStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 8,
  fontSize: 13,
  fontFamily: "var(--font-sans)",
  marginBottom: 12,
};

const backStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: "var(--color-text-secondary)",
  fontSize: 13,
  fontWeight: 500,
  fontFamily: "var(--font-sans)",
  padding: 0,
};

const segStyle: CSSProperties = {
  fontSize: 13,
  textDecoration: "none",
};

const currentStyle: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-secondary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: 320,
};
